import { OPEN_PARAM, openParamId, type OpenParamKey } from '@components/Chat/core'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'

/**
 * Opens one item's details when the page's query names it — as a card in the Chat asks
 * the Expenses page for an expense or a pay-up, and the itinerary for an activity — once
 * `items` has loaded (undefined while it hasn't). The parameter is taken off the URL
 * either way, so going back doesn't open it again.
 *
 * The item opens on the render after the parameter is dropped: a router's
 * `setSearchParams` builds on the params of the render it was called in, so an `open`
 * that writes the URL too (the itinerary selecting the activity's day) must not run in
 * the same tick, or it would write the parameter back.
 */
export const useOpenFromParam = <T extends { id: number }>(
	key: OpenParamKey,
	items: readonly T[] | undefined,
	open: (item: T) => void,
): void => {
	const [params, setParams] = useSearchParams()
	const id = openParamId(params, key)
	const openRef = useRef(open)
	openRef.current = open
	const [opening, setOpening] = useState<T | null>(null)

	useEffect(() => {
		if (id === null || items === undefined) return
		setParams(
			prev => {
				const next = new URLSearchParams(prev)
				next.delete(OPEN_PARAM[key])
				return next
			},
			{ replace: true },
		)
		const item = items.find(candidate => candidate.id === id)
		if (item) setOpening(item)
		else toast.error("Couldn't open it. Check your connection and try again.")
	}, [id, items, key, setParams])

	useEffect(() => {
		if (!opening) return
		setOpening(null)
		openRef.current(opening)
	}, [opening])
}
